import React, { lazy, Suspense } from 'react';

/**
 * Create a lazy-loaded component with fallback
 * @param {Function} importFunc - Dynamic import function
 * @param {React.Component} fallback - Loading fallback component
 */
export function lazyLoad(importFunc, fallback = null) {
  const LazyComponent = lazy(importFunc);

  return (props) => (
    <Suspense fallback={fallback || <LoadingSpinner />}>
      <LazyComponent {...props} />
    </Suspense>
  );
}

/**
 * Loading spinner component
 */
export function LoadingSpinner() {
  return (
    <div className="lazy-loading-spinner" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      padding: '40px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(254, 85, 0, 0.2)',
        borderTopColor: '#FE5500',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Page loading component with branding
 */
export function PageLoader() {
  return (
    <div className="page-loader" style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)',
      color: '#fff'
    }}>
      <div style={{
        width: '60px',
        height: '60px',
        border: '4px solid rgba(254, 85, 0, 0.2)',
        borderTopColor: '#FE5500',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px'
      }} />
      <p style={{ color: 'rgba(255, 255, 255, 0.7)' }}>Loading...</p>
    </div>
  );
}

/**
 * Preload a component for faster subsequent navigation
 * @param {Function} importFunc - Dynamic import function
 */
export function preloadComponent(importFunc) {
  return importFunc();
}

/**
 * Create multiple lazy components at once
 * @param {Object} imports - Object with component names as keys and import functions as values
 */
export function createLazyComponents(imports) {
  const components = {};
  
  Object.keys(imports).forEach(key => {
    components[key] = lazyLoad(imports[key]);
  });
  
  return components;
}

export default lazyLoad;
